// ---------------------------------------------------------------------------
// Segmented control, for a short choice where every option should be readable
// at once (Public / Private, All / Waiting / Space for me, Easy / Medium /
// Hard / AI).
//
// It is a radio group rather than a row of buttons, so the whole control is one
// stop for the keyboard and a screen reader announces which option is live.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /**
   * One option the control offers but cannot take yet, while the rest stay
   * live. The lobby's AI opponent is the case: a signed-out host sees it on
   * screen, greyed, with the reason beside it, because hiding it made a room
   * look like a site with no AI opponents in it. Do not confuse it with the
   * control-wide `disabled`, which means the whole choice is unavailable.
   */
  disabled?: boolean;
}

export function Segmented<T extends string>(props: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
}): ReactElement {
  const { options, value, onChange, label, size = 'md', disabled = false } = props;

  return (
    <div role="radiogroup" aria-label={label} className="inline-flex gap-1.5 rounded-[9px] bg-surface-2 p-1">
      {options.map((option) => {
        const selected = option.value === value;
        const off = disabled || option.disabled === true;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={off}
            onClick={() => onChange(option.value)}
            className={[
              'min-h-11 rounded-chip px-3 font-semibold',
              'transition-[background-color,color] duration-150 ease-out',
              size === 'sm' ? 'text-[12px]' : 'text-[13px]',
              // `--c-tile-letter` is the palette's near-white and the only text
              // colour that holds up on a filled blue in both themes.
              selected ? 'bg-blue-500 text-ink-on-fill' : 'text-ink-muted',
              off ? 'cursor-not-allowed opacity-60' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
